import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import {
  PartnerEmptyState,
  PartnerLoading,
  PartnerPageHeader,
  PartnerPageShell,
  partnerOxbloodBtnSx,
} from '../components/PartnerListChrome';
import PartnerTripRequestDrawer from '../components/PartnerTripRequestDrawer';
import CreateOpsRequestDialog from '../components/CreateOpsRequestDialog';
import { compareAttentionItems } from '../utils/requestSla';

const FILTER_OPTIONS = [
  { id: 'active', status: null },
  { id: 'open', status: 'open' },
  { id: 'assigned', status: 'assigned' },
  { id: 'in_progress', status: 'in_progress' },
  { id: 'awaiting_supplier', status: 'awaiting_supplier' },
  { id: 'awaiting_traveler', status: 'awaiting_traveler' },
];

const PartnerRequests = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');
  const [includeResolved, setIncludeResolved] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      const option = FILTER_OPTIONS.find((item) => item.id === activeFilter);
      if (option?.status) params.set('status', option.status);
      if (includeResolved) params.set('includeResolved', '1');
      const qs = params.toString();
      const response = await api.get(`/partners/requests${qs ? `?${qs}` : ''}`, { usePartnerAuth: true });
      const rows = response.data?.requests || [];
      setRequests([...rows].sort((a, b) => compareAttentionItems(
        { severity: a.severity, createdAt: a.createdAt },
        { severity: b.severity, createdAt: b.createdAt }
      )));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerOps.loadFailed'));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, includeResolved, t]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const openRequest = (requestId) => {
    setSelectedRequestId(requestId);
    setDrawerOpen(true);
  };

  const renderSeverity = (request) => (
    <Chip
      size="small"
      label={t(`partnerOps.severity.${request.severity}`, { defaultValue: request.severity })}
      sx={{ height: 22, borderRadius: 0, fontSize: 11 }}
    />
  );

  const renderRowCells = (request) => (
    <>
      <TableCell>{renderSeverity(request)}</TableCell>
      <TableCell sx={{ fontSize: 13 }}>
        {t(`partnerOps.requestType.${request.type}`, { defaultValue: request.type })}
      </TableCell>
      <TableCell sx={{ fontSize: 13 }}>{request.clientName}</TableCell>
      <TableCell sx={{ fontSize: 13 }}>{request.tripTitle}</TableCell>
      <TableCell sx={{ fontSize: 13 }}>{request.title}</TableCell>
      <TableCell sx={{ fontSize: 13 }}>{request.age}</TableCell>
      <TableCell sx={{ fontSize: 13 }}>
        {request.assignedUserName || t('partnerOps.unassigned')}
      </TableCell>
      <TableCell sx={{ fontSize: 13 }}>
        {t(`partnerOps.status.${request.status}`, { defaultValue: request.status })}
      </TableCell>
    </>
  );

  return (
    <PartnerPageShell error={error} onClearError={() => setError('')}>
      <Box data-testid="partner-requests-page">
      <PartnerPageHeader
        compact
        title={t('partnerOps.requestsTitle')}
        actions={(
          <Button
            sx={partnerOxbloodBtnSx}
            onClick={() => setCreateOpen(true)}
            data-testid="partner-requests-new"
          >
            {t('partnerOps.newRequest')}
          </Button>
        )}
      />

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', py: 1.5, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={activeFilter}
            onChange={(event) => setActiveFilter(event.target.value)}
            displayEmpty
            data-testid="partner-requests-filter"
            sx={{ borderRadius: 0, bgcolor: DOCUMENT.surface, fontSize: 13 }}
          >
            {FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {t(`partnerOps.filter.${option.id}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box
          component="button"
          type="button"
          aria-pressed={includeResolved}
          onClick={() => setIncludeResolved((prev) => !prev)}
          sx={{
            background: includeResolved ? DOCUMENT.oxblood : 'none',
            border: `1px solid ${includeResolved ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
            color: includeResolved ? DOCUMENT.ivory : DOCUMENT.ink2,
            font: 'inherit',
            fontSize: 12,
            px: 1.6,
            py: 0.75,
            cursor: 'pointer',
          }}
        >
          {t('partnerOps.includeResolved')}
        </Box>
      </Box>

      {loading ? (
        <PartnerLoading />
      ) : !requests.length ? (
        <PartnerEmptyState
          title={t('partnerOps.emptyRequests')}
          body={t('partnerOps.emptyRequestsBody')}
        />
      ) : (
        <>
          <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 0 }}>
            <Table size="small" sx={{ border: `1px solid ${DOCUMENT.ruleHard}` }}>
              <TableHead>
                <TableRow sx={{ bgcolor: DOCUMENT.canvas }}>
                  <TableCell>{t('partnerOps.colSeverity')}</TableCell>
                  <TableCell>{t('partnerOps.colType')}</TableCell>
                  <TableCell>{t('partnerOps.colClient')}</TableCell>
                  <TableCell>{t('partnerOps.colTrip')}</TableCell>
                  <TableCell>{t('partnerOps.colTitle')}</TableCell>
                  <TableCell>{t('partnerOps.colAge')}</TableCell>
                  <TableCell>{t('partnerOps.colOwner')}</TableCell>
                  <TableCell>{t('partnerOps.colStatus')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => (
                  <TableRow
                    key={request.id}
                    hover
                    onClick={() => openRequest(request.id)}
                    sx={{ cursor: 'pointer' }}
                    data-testid={`partner-request-row-${request.id}`}
                  >
                    {renderRowCells(request)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, mt: 1 }}>
            {requests.map((request) => (
              <Box
                key={request.id}
                component="button"
                type="button"
                onClick={() => openRequest(request.id)}
                data-testid={`partner-request-card-${request.id}`}
                sx={{
                  textAlign: 'left',
                  bgcolor: DOCUMENT.surface,
                  border: `1px solid ${DOCUMENT.ruleHard}`,
                  p: 2,
                  cursor: 'pointer',
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  {renderSeverity(request)}
                  <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, ml: 'auto' }}>
                    {request.age}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{request.title}</Typography>
                <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.5 }}>
                  {request.clientName} · {request.tripTitle}
                </Typography>
                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.5 }}>
                  {request.assignedUserName || t('partnerOps.unassigned')}
                  {' · '}
                  {t(`partnerOps.status.${request.status}`, { defaultValue: request.status })}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      <PartnerTripRequestDrawer
        open={drawerOpen}
        requestId={selectedRequestId}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRequestId(null);
        }}
        onChanged={loadRequests}
      />

      <CreateOpsRequestDialog
        open={createOpen}
        mode="partner"
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          loadRequests();
          if (created?.id) {
            setSelectedRequestId(created.id);
            setDrawerOpen(true);
          }
        }}
      />
      </Box>
    </PartnerPageShell>
  );
};

export default PartnerRequests;
